import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveBudgets } from '@/hooks/useBudgets';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { LoadingCard } from '@/components/ui/Loading';
import { NoTransactionsEmpty } from '@/components/ui/EmptyState';
import { TransactionList } from '@/components/transaction/TransactionList';
import { useTransactions } from '@/hooks/useTransactions';
import { Page } from '@/components/layout/Page';

export function TransactionsPage() {
  const navigate = useNavigate();
  const { data: budgets = [], isLoading: budgetsLoading } = useActiveBudgets();
  const [selectedBudgetId, setSelectedBudgetId] = React.useState<string>('');
  const [selectedChildBudgetId, setSelectedChildBudgetId] = React.useState<string>('');
  
  const { data: transactions = [], isLoading: transactionsLoading } = useTransactions(
    selectedChildBudgetId || undefined
  );

  const rootBudgets = React.useMemo(
    () => budgets.filter((budget) => !budget.parentId),
    [budgets]
  );

  const selectedBudget = React.useMemo(
    () => rootBudgets.find((budget) => budget.id === selectedBudgetId),
    [rootBudgets, selectedBudgetId]
  );

  const childBudgets = React.useMemo(() => {
    if (!selectedBudget?.children?.length) return [];
    const result: typeof selectedBudget.children = [];
    const walk = (nodes: typeof selectedBudget.children) => {
      nodes.forEach((node) => {
        result.push(node);
        if (node.children?.length) {
          walk(node.children);
        }
      });
    };
    walk(selectedBudget.children);
    return result;
  }, [selectedBudget]);

  const budgetOptions = rootBudgets.map((b) => ({ value: b.id, label: b.name }));
  const childBudgetOptions = childBudgets.map((b) => ({ value: b.id, label: b.name }));

  React.useEffect(() => {
    setSelectedChildBudgetId('');
  }, [selectedBudgetId]);

  const handleCreateClick = () => {
    if (selectedChildBudgetId) {
      navigate(`/budgets/${selectedChildBudgetId}?addTransaction=true`);
    } else if (childBudgets.length > 0) {
      navigate(`/budgets/${childBudgets[0].id}?addTransaction=true`);
    } else {
      navigate('/budgets/new');
    }
  };

  const isLoading = budgetsLoading || (selectedChildBudgetId && transactionsLoading);

  return (
    <Page className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Transactions</h1>
          <p className="text-sm text-muted-foreground">
            View and manage your expenses
          </p>
        </div>
        <Button onClick={handleCreateClick}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-1"
          >
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          Add
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <Select
          options={budgetOptions}
          value={selectedBudgetId}
          onChange={(e) => setSelectedBudgetId(e.target.value)}
          placeholder="Select an active budget"
          className="max-w-xs"
        />
        <Select
          options={childBudgetOptions}
          value={selectedChildBudgetId}
          onChange={(e) => setSelectedChildBudgetId(e.target.value)}
          placeholder={selectedBudgetId ? 'Select a child budget' : 'Select a budget first'}
          className="max-w-xs"
          disabled={!selectedBudgetId}
        />
      </div>

      {/* Transactions list */}
      {isLoading ? (
        <LoadingCard className="min-h-[200px]" />
      ) : !selectedChildBudgetId ? (
        <div className="text-center py-8 text-muted-foreground">
          Select a child budget to view transactions
        </div>
      ) : transactions.length === 0 ? (
        <NoTransactionsEmpty onCreateClick={handleCreateClick} />
      ) : (
        <TransactionList
          transactions={transactions}
          onTransactionClick={(t) => navigate(`/transactions/${t.id}`)}
          currentBudgetId={selectedBudgetId}
        />
      )}
    </Page>
  );
}
